import React from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import APIService from '../../../services/APIService';
import ReCAPTCHA from "react-google-recaptcha";
import { CSSTransition } from 'react-transition-group';
import DMSpinnerButton from '../../common/DMSpinnerButton/DMSpinnerButton';
import ForgotPassword from '../ForgotPassword/ForgotPassword';
import CustomInput from '../CustomInput/CustomInput';
import sendForgotPasswordEmail from '../sendForgotPasswordEmail';
import useSessionStorage from '../../../hooks/useSessionStorage';
import { PASSWORD_CHANGE_FLOW_KEY } from '../passwordChangeFlow';
import CheckIcon from '../../../assets/Icons/check.svg';
import { isLoginAsToken } from '../../Administration/PassportManagment/LoginAsButton';

import './LoginForm.scss';

const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

const LoginSchema = Yup.object().shape({
    // recaptcha: isLocalhost
    //     ? Yup.string().nullable()
    //     : Yup.string().required('Required').typeError(''),
    username: Yup.string().required('Required'),
    password: Yup.string().required('Required')
});

const Login = () => {
    const reCaptchaRef = React.useRef();
    const usernameRef = React.useRef();
    const passwordRef = React.useRef();
    const [loginState, setLoginState] = React.useState({
        loading: false,
        APIError: null,
        autofillActive: false,
    });
    const [isPasswordExpired, setIsPasswordExpired] = React.useState(false);
    const [successMessage, setSuccessMessage] = React.useState(null);
    const [revealPassword, setRevealPassword] = React.useState(false);
    const [showForgotPassword, setShowForgotPassword] = React.useState(false);
    const [passwordChangeFlow, setPasswordChangeFlow] = useSessionStorage(PASSWORD_CHANGE_FLOW_KEY, false);
    const [passwordChange, setPasswordChange] = React.useState(passwordChangeFlow);
    const [hash, setHash] = React.useState(null);

    const getHashedTokenFromUrl = () => {
        const queryString = window.location.search;
        const match = queryString.match(/[?&]pspToken=([^&#]*)/);
        const param = match ? match[1] : null;

        return param;
    }

    const getLoginAsTokenFromUrl = () => {
        const match = window.location.hash.match(/[#&]loginAs=([^&]*)/);
        const param = match ? decodeURIComponent(match[1]) : null;

        return param;
    }

    React.useEffect(() => {
        const loginAsToken = getLoginAsTokenFromUrl();

        if (loginAsToken) {
            window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
            handleLoginAs(loginAsToken);
        }
    }, [])

    React.useEffect(() => {
        const param = getHashedTokenFromUrl();
        setHash(param);

        if (param) {
            setShowForgotPassword(true);
        }

        if (passwordChangeFlow) {
            setPasswordChangeFlow(false);
            setShowForgotPassword(true);
        }

        const checkAutofillEvent = () => {
            //https://stackoverflow.com/questions/35049555/chrome-autofill-autocomplete-no-value-for-password

            setTimeout(() => {
                const textbox1 = usernameRef.current;

                if (textbox1 && (window.getComputedStyle(textbox1).backgroundColor === "rgb(250, 255, 189)" ||
                    window.getComputedStyle(textbox1).backgroundColor === "rgb(232, 240, 254)"
                    || isWebkitAutofill())) // autofill color
                {
                    setLoginState({
                        ...loginState,
                        autofillActive: true
                    })
                }
            }, 1000);
        }

        const isWebkitAutofill = () => {
            try {
                return document.querySelectorAll("input:-webkit-autofill").length > 0;
            }
            catch (ex) { return false; }
        }

        checkAutofillEvent();

    }, [])

    const getLoginQueryWithVars = (formData) => {
        return {
            query: `
                query ($password: String!, $username: String!, $loginAsAppId: String!) {
                    login(password: $password,
                          username: $username,
                          loginAsAppId: $loginAsAppId) {
                      token
                      status
                      username
                      isPasswordExpired
                      message
                    }
                }
            `,
            variables: {
                password: formData.password.trim(),
                username: formData.username.trim().toLowerCase(),
                loginAsAppId: "DMarket"
            }
        };

    }

    const handleLogin = async (formData) => {
        setLoginState({
            ...loginState,
            loading: true,
            APIError: null
        });

        var payload = getLoginQueryWithVars(formData);

        try {
            const auth = await APIService.post('https://passport-api.services.r2net.com/auth', payload);

            if (auth?.data?.login?.isPasswordExpired) {
                setIsPasswordExpired(true);
                handleLoginError(auth?.data?.login?.message || 'Your password has expired. Please reset it.');
                return;
            }

            const token = auth?.data?.login?.status ? auth?.data?.login?.token : null;

            if (!token) {
                const params = {
                    username: formData.username.trim(),
                    password: formData.password.trim(),
                    captchaResult: formData.recaptcha
                }

                const response = await APIService.post('/Login/LoginAjax', params);
                if (response.result !== null && response.result !== undefined && response.result === "ok") {
                    window.location.href = response.redirectUrl;
                }
                else {
                    const passportMessage = auth?.data?.login?.message?.toLowerCase() || '';
                    if (passportMessage.includes('your account will be locked')) {
                        handleLoginError(auth.data.login.message);
                    } else if (passportMessage.includes('please reach out to your administrator')) {
                        handleLoginError('Your account has been locked. To restore access, please email helpdesk@r2net.com with your DMarket username.');
                    } else if (auth?.errors?.length) {
                        handleLoginError('Failed to login, please contact administration.');
                    } else {
                        handleLoginError('Username or password is incorrect');
                    }
                }
                return;
            }

            var loginData = {
                token: token,
                captchaResult: formData.recaptcha
            };

            const loginResponse = await APIService.post('/Login/LoginToken', loginData);

            if (loginResponse.result === "ok") {
                window.location.href = "/";
            } else {
                handleLoginError('Username or password is incorrect');
            }
        } catch (error) {
            handleLoginError('Failed to login, please contact administration.');
        }
    };

    const handleLoginAs = async (token) => {
        setLoginState({
            ...loginState,
            loading: true,
            APIError: null
        });

        if (!isLoginAsToken(token)) {
            handleLoginError('Failed to login, please contact administration.');
            return;
        }

        try {
            const loginResponse = await APIService.post('/Login/LoginToken', { token: token });

            if (loginResponse.result === "ok") {
                window.location.href = "/";
            } else {
                handleLoginError('Failed to login, please contact administration.');
            }
        } catch (error) {
            handleLoginError('Failed to login, please contact administration.');
        }
    };

    const handleLoginError = (errorMsg) => {
        setLoginState({
            ...loginState,
            loading: false,
            APIError: errorMsg
        })
        setFieldValue('recaptcha', isLocalhost ? 'localhost' : '');
        if (reCaptchaRef.current) {
            reCaptchaRef.current.reset();
        }
    }

    const handleRevealPasswordClicked = () => {
        setRevealPassword(prevState => !prevState);
    }

    const handleRecaptchaExpired = () => {
        setFieldValue('recaptcha', '')
    }

    const handleForgotPasswordClick = (e) => {
        e.preventDefault();
        // Remove query string from URL
        window.history.replaceState({}, document.title, window.location.pathname);
        setHash(null);
        setShowForgotPassword(true);
    }

    const handleRenewPasswordClick = async (e) => {
        e.preventDefault();
        try {
            const data = await sendForgotPasswordEmail(values.username);
            if (data?.data?.forgotPassword?.status) {
                setLoginState(prev => ({ ...prev, APIError: null }));
                setSuccessMessage('An email to reset your password has been sent. Please check your inbox and follow the instructions.');
            } else {
                setLoginState(prev => ({
                    ...prev,
                    APIError: 'Failed to send email. Please try again.',
                }));
            }
        } catch (error) {
            setLoginState(prev => ({
                ...prev,
                APIError: 'An error occurred. Please try again later.',
            }));
        }
    }

    const handleBackToLogin = () => {
        setShowForgotPassword(false);
        setPasswordChange(false);
    }

    const { handleSubmit, handleChange, handleBlur, values, errors, touched, setFieldValue, isValid } = useFormik({
        initialValues: {
            recaptcha: isLocalhost ? 'localhost' : '',
            username: '',
            password: '',
        },
        validationSchema: LoginSchema,
        onSubmit: handleLogin,
        validateOnMount: true,
        validateOnBlur: false
        //isInitialValid: false,
    });

    const getAccountLinkText = () => {
        if (successMessage) return null;
        if (isPasswordExpired) return 'Please click here to renew your password';
        return "Can't access your account?";
    }

    const isFormValid = () => {
        const isAutofillEvent = loginState.autofillActive && !touched.username && !touched.password;

        if (!isAutofillEvent) {
            return isValid;
        }
        else {
            return errors.recaptcha !== 'Required';
        }
    }

    return (
        <div className='login-form-container' id='form-container'>
            {showForgotPassword ? (
                <ForgotPassword onBack={handleBackToLogin} hash={hash} passwordChange={passwordChange} />
            ) : (
                <div className='form-content'>
                    <CSSTransition in={true} timeout={8500} classNames="form-elem-transition" appear>
                        <div className='form-header transition-delay'>
                            <span className='login-txt'>Login</span>
                        </div>
                    </CSSTransition>
                    <form onSubmit={handleSubmit}>
                        <CSSTransition in={true} timeout={8500} classNames="form-elem-transition" appear>
                            <CustomInput
                                inputRef={usernameRef}
                                handleChange={handleChange}
                                className='username-i transition-delay'
                                type='text'
                                label='Username'
                                name='username'
                                placeholder='Your Username'
                                value={values.username}
                                handleBlur={handleBlur}
                                errors={errors}
                                touched={touched} />
                        </CSSTransition>

                        <CSSTransition in={true} timeout={8500} classNames="form-elem-transition" appear>
                            <CustomInput
                                inputRef={passwordRef}
                                handleChange={handleChange}
                                className='password-i transition-delay'
                                type='password'
                                label='Password'
                                name='password'
                                placeholder='Your Password'
                                value={values.password}
                                handleBlur={handleBlur}
                                errors={errors}
                                touched={touched}
                                revealPassword={revealPassword}
                                onRevealPasswordClick={handleRevealPasswordClicked}
                            />
                        </CSSTransition>

                        {
                            loginState.APIError && (
                                <div className='error-box'>
                                    <span className='err-text'>{loginState.APIError}</span>
                                </div>
                            )
                        }

                        {
                            successMessage && (
                                <div className='success-message'>
                                    <img src={CheckIcon} alt="" />
                                    <span className='success-text'>{successMessage}</span>
                                </div>
                            )
                        }

                        {/* {!isLocalhost && (
                            <CSSTransition in={true} timeout={8500} classNames="form-elem-transition" appear>
                                <div className='recapcha-container transition-delay'>
                                    <ReCAPTCHA
                                        ref={reCaptchaRef}
                                        sitekey={'6Le_Cy0UAAAAAGjt-7URMsewmNrxxNav8u-XREZO'}
                                        theme={'light'}
                                        onChange={(value) => {
                                            setFieldValue('recaptcha', value);
                                        }}
                                        onExpired={handleRecaptchaExpired}
                                    />
                                </div>
                            </CSSTransition>
                        )} */}

                        <CSSTransition in={true} timeout={8500} classNames="form-elem-transition" appear>
                            <div className='access-your-account-section transition-delay'>
                                {getAccountLinkText() && (
                                    <a className='reset-link-txt' href='#' onClick={isPasswordExpired ? handleRenewPasswordClick : handleForgotPasswordClick}>
                                        {getAccountLinkText()}
                                    </a>
                                )}
                            </div>
                        </CSSTransition>

                        <CSSTransition in={true} timeout={8500} classNames="form-elem-transition" appear>
                            <div className='btn-container transition-delay'>
                                <DMSpinnerButton
                                    className={`login-btn ${loginState.loading && 'disabled'}`}
                                    text={loginState.loading ? '' : 'Sign In'}
                                    type={'submit'}
                                    disabled={loginState.loading || !isFormValid() || !!successMessage}
                                    transitionIn={loginState.loading}
                                    transitionClassName={'login-anim'}
                                    withTransition={true} />
                            </div>
                        </CSSTransition>
                    </form>
                </div>
            )}
        </div>
    )
}

export default Login
